<template>
  <div class="d-flex flex-column justify-start px-6">
    <p class="h6 pa-0 mt-4">
      {{ $t('ai_features.user_access_control.title') }}
    </p>

    <v-card
      class="mt-4 pa-4 align-center d-flex"
      outlined
      width="500px"
      min-height="153px"
    >
      <v-row
        v-if="isLoadingAISettings && !organization.aiSettings"
        class="d-flex justify-center align-center"
      >
        <v-progress-circular
          color="primary"
          indeterminate
          :size="40"
          :width="2"
        />
      </v-row>

      <div v-else class="w-100">
        <v-row class="d-flex justify-start align-center mt-0 mb-6">
          <v-col cols="5">
            <span class="body-2">
              {{
                $t('ai_features.user_access_control.users_tokens_monthly_limit')
              }}
            </span>
          </v-col>

          <v-col cols="7">
            <v-form
              v-model="isLimitValid"
              class="d-flex align-center"
              ref="limitForm"
            >
              <HumanizedNumericTextField
                class="ma-0 pa-0"
                :allowDecimals="false"
                :value="aiSettingsForm.usersTokensMonthlyLimit"
                :vuetifyTextFieldProps="vuetifyTextFieldProps"
                @change="handleMonthlyLimitChange"
              />

              <Button
                class="ms-1"
                color="primary"
                plain
                size="small"
                :disabled="!isLimitValid"
                :loading="isLoadingAISettings"
                :text="$t('common.button.save')"
                @click="saveAISettings"
              />
            </v-form>
          </v-col>
        </v-row>

        <v-row class="d-flex align-center">
          <v-col cols="5">
            <span class="body-2">
              {{
                $t(
                  'ai_features.user_access_control.ai_access_to_all_users_label'
                )
              }}
            </span>
          </v-col>

          <v-col cols="7">
            <v-switch
              v-model="aiSettingsForm.canAllUsersUseAIFeatures"
              dense
              readonly
              @click="showAllUsersAccessConfirmation = true"
            ></v-switch>
          </v-col>
        </v-row>
      </div>
    </v-card>

    <v-card class="my-8 pa-0" outlined>
      <div class=" py-3 px-4">
        <h4 class="font-weight-medium">
          {{ $t('ai_features.user_access_control.access_control_title') }}
        </h4>

        <p class="blue-grey--text body-3 pt-2" style="max-width: 70%;">
          {{ $t('ai_features.user_access_control.access_control_subtitle') }}
        </p>
      </div>

      <v-row no-gutters class="ma-0 pa-0 pb-4">
        <v-col cols="1" class="pa-0 tabs-container">
          <v-tabs
            v-model="activeAccessTab"
            class="ma-0 pa-0"
            slider-color="primary"
            slider-size="3"
            style="min-height: 300px"
            top
            vertical
          >
            <v-tab
              v-for="tab in accessTabs"
              active-class="grey--text text--darken-4"
              class="vertical-tab"
              :key="tab.value"
            >
              {{ tab.label }}
            </v-tab>
          </v-tabs>
        </v-col>

        <v-col cols="11" class="py-0 px-4">
          <v-tabs-items v-model="activeAccessTab">
            <v-tab-item v-for="tab in accessTabs" :key="`item-${tab.value}`">
              <component :is="tab.component" />
            </v-tab-item>
          </v-tabs-items>
        </v-col>
      </v-row>
    </v-card>

    <GrantAccessToAllUsersConfirmation
      :shown="showAllUsersAccessConfirmation"
      :newAccessValue="aiSettingsForm.canAllUsersUseAIFeatures"
      @confirm="handleAllUsersAccessChange"
      @cancel="handleCancelGrantAllUsersAccess"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import mixin from '@/mixins/mixin.vue'

import Button from '@/components/UI/Button.vue'

import HumanizedNumericTextField from '@/components/UI/HumanizedNumericTextField.vue'
import GrantAccessToAllUsersConfirmation from './GrantAccessToAllUsersConfirmation.vue'
import AccessTable from './AccessTable.vue'
import SiteAccessTable from './SiteAccessTable.vue'

export default {
  name: 'UserAccessTab',
  mixins: [mixin],

  components: {
    AccessTable,
    Button,
    GrantAccessToAllUsersConfirmation,
    HumanizedNumericTextField,
    SiteAccessTable,
  },

  data() {
    return {
      isLoadingAISettings: false,
      isConfirmationShown: false,
      isLimitValid: false,
      currentAccountsCreated: 0,
      showAllUsersAccessConfirmation: false,
      activeAccessTab: 0,
      aiSettingsForm: {},
    }
  },

  watch: {
    organizationAISettings: {
      immediate: true,
      deep: true,
      handler(newAISettings) {
        this.aiSettingsForm = { ...(newAISettings ?? {}) }
      },
    },
  },

  computed: {
    ...mapGetters(['account', 'organization', 'organizationId']),

    organizationName() {
      return this.organization?.name || ''
    },

    deleteTitleText() {
      return this.$t('ai_features.delete_user_from_access.title')
    },

    deleteMessageText() {
      return this.$t('ai_features.delete_user_from_access.message', {
        userName: this.userToDelete.displayname,
      })
    },

    organizationAISettings() {
      return this.organization?.aiSettings || {}
    },

    accessTabs() {
      return [
        {
          value: 'users',
          label: this.$t('common.users.user_other'),
          component: AccessTable,
        },
        {
          value: 'sites',
          label: this.$t('common.site_other'),
          component: SiteAccessTable,
        },
      ]
    },

    vuetifyTextFieldProps() {
      return {
        dense: true,
        disabled: this.isLoadingAISettings,
        hideDetails:
          !this.organization.aiSettings ||
          this.isLoadingAISettings ||
          this.isLimitValid,
        outlined: true,
        rules: [this.validateMonthlyLimit],
      }
    },
  },

  methods: {
    ...mapActions(['updateOrganizationAISettings']),

    goToProfile(user) {
      this.navigateTo({
        name: 'profile',
        params: {
          organizationId: user.organization.id,
          userId: user.id,
        },
      })
    },

    handleMonthlyLimitChange(newLimit) {
      this.aiSettingsForm = {
        ...this.aiSettingsForm,
        usersTokensMonthlyLimit: newLimit,
      }
    },

    validateMonthlyLimit() {
      return (
        this.aiSettingsForm.usersTokensMonthlyLimit > 0 ||
        this.$t(
          'ai_features.user_access_control.tokens_monthly_minimum_limit_error'
        )
      )
    },

    async saveAISettings() {
      this.isLoadingAISettings = true
      try {
        await this.updateOrganizationAISettings({
          organizationId: this.organizationId,
          organizationAISettings: this.aiSettingsForm,
        })

        this.showSuccess(
          this.$t('ai_features.user_access_control.settings_save_success')
        )
        this.$emit('aiTokenLimitUpdated')
      } catch (error) {
        this.showError(
          this.$t('ai_features.user_access_control.settings_save_error')
        )
      } finally {
        this.isLoadingAISettings = false
      }
    },

    handleAllUsersAccessChange(canAllUsersUseAIFeatures) {
      this.showAllUsersAccessConfirmation = false
      this.aiSettingsForm = {
        ...this.aiSettingsForm,
        canAllUsersUseAIFeatures,
      }
      this.saveAISettings()
    },

    handleCancelGrantAllUsersAccess() {
      this.showAllUsersAccessConfirmation = false
    },

    removeUserFromAIAccess() {
      this.isDeleting = true
      setTimeout(() => {
        const clonedUsers = _.cloneDeep(this.users)
        this.users = clonedUsers.filter(
          user => user.account.id !== this.userToDelete.userId
        )
        this.showSuccess(
          this.$t('ai_features.delete_user_from_access.delete_user_success', {
            userName: this.userToDelete.displayname,
          })
        )
        this.isDeleting = false
        this.showDeleteConfirmationModal = false
      }, 2000)
    },
  },
}
</script>
<style lang="scss" scoped>
.menu-item {
  border-radius: 4px;
  &:first-child {
    margin-top: 0 !important;
  }
  &:hover {
    background-color: $color-blue-grey-lighten3;
  }
}
.vertical-tab {
  justify-content: flex-start;
  text-align: left;
}

.tabs-container {
  border-right: 1px solid $color-blue-grey-lighten2;
}
</style>
<style lang="scss">
.v-small-dialog__content {
  padding: 6px;
}
.v-small-dialog__actions {
  padding: 0px !important;
}
</style>
